import { useEffect, useMemo, useState } from "react";
import { FBABoxDTO, PackingGroup } from "@boxem/core-api-schema";

import styles from "./BulkAssignModal.module.css";

import { FbaBatch, FbaBatchItem, getItemAssignedQuantity, itemAssignedQuantityKey, useShipmentContext } from "@/contexts/ShipmentContext";
import Button from "@/components/forms/Button";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import IconBoxPlaceItem from "@/components/icons/IconBoxPlaceItem";
import IconInfo from "@/components/icons/IconInfo";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import IconCaution from "@/components/icons/IconCaution";
import IconPlusCircle from "@/components/icons/IconPlusCircle";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Tooltip from "@/components/Tooltip";
import IconDelete from "@/components/icons/iconDelete";

import ProductView from "../ProductView";
import { getDefaultDimensionsAndWeight } from "../../utils";
import { handleCreateBox } from "@/api/fulfillment";
import { PrinterContextProps, usePrinterContext } from "@/contexts/PrinterContext";
import SelectItems, { SelectableItem } from "./SelectItemsModalBody";
import { Item } from "./PackingGroup";
import { filterMap } from "@/utils";

type BoxType = {
  units: number;
  boxes: number;
  length: number;
  width: number;
  height: number;
  lb: number;
  oz: number;
  oversized: boolean;
};

export type BulkAssignModalProps = {
  packingGroupId?: string;
  items?: Item[];
  shipmentId?: number;
  index?: number;
  open?: boolean;
  onClose: () => void;
  onError: (errors: any) => void,
};

export default function BulkAssignModal({
  packingGroupId,
  items: propsItems,
  shipmentId,
  index,
  open,
  onClose: _onClose,
  onError,
}: BulkAssignModalProps) {
  const loadItems = () => (propsItems ?? [])
    .map(item => ({ ...item, selected: !item.done }))
    .sort((l, r) => l.done === r.done ? 0 : l.done ? 1 : -1);

  const totalUnits = propsItems?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
  const [items, setItems] = useState<SelectableItem[]>(loadItems);
  const [selectingItems, setSelectingItems] = useState(true);
  const [itemsSelected, setItemsSelected] = useState<Item[]>(null);
  const [boxTypes, setBoxTypes] = useState<BoxType[][]>(null);
  const { printFNSKULabel } = usePrinterContext();
  const { setCurrentBatch, currentBatch: batch } = useShipmentContext();

  useEffect(() => setItems(loadItems()), [propsItems, open]);

  const onClose = () => {
    _onClose();
    setTimeout(() => {
      if (packingGroupId) {
        setBoxTypes(null);
        setSelectingItems(true);
        setItems([]);
      }
    }, 400); // gives time for the dialog transition to finish before changing state
  };

  const selectAll = (selected: boolean) => setItems(items.map(item => ({...item, selected: selected && !item.done})));
  const defaults = useMemo(() => getDefaultDimensionsAndWeight(), []);
  const defaultBoxType: () => BoxType = () => {
    return {
      units: 0,
      boxes: 0,
      length: defaults.length,
      width: defaults.width,
      height: defaults.height,
      lb: Math.floor(defaults.weight),
      oz: (defaults.weight - Math.floor(defaults.weight)) * 16,
      oversized: false,
    };
  };
  const continueClicked = () => {
    setSelectingItems(false);
    const newItemsSelected = filterMap(items, item => item.selected ? item : null);
    setItemsSelected(newItemsSelected);
    setBoxTypes(newItemsSelected.map(() => [defaultBoxType()]));
  };
  const assignClicked = async () => {
    await doBulkAssign({
      items: itemsSelected.map(item => item.batchItem),
      boxTypes,
      batch,
      onError,
      packingGroupId: packingGroupId,
      setCurrentBatch,
      shipmentId,
      printFNSKULabel,
    });
    onClose();
  };

  const newBoxType = (i: number) => {
    const newBoxTypes = [...boxTypes];
    newBoxTypes[i].push(defaultBoxType());
    setBoxTypes(newBoxTypes);
  };

  const itemsSelectedCount = items.reduce((acc, item) => acc + (item.selected ? 1 : 0), 0);
  const zeroItemsToAssign = useMemo(() => null == boxTypes?.find(bts => bts.find((bt => bt.units * bt.boxes > 0))), [boxTypes]);
  const tooManyAssigned = useMemo(() => null != boxTypes?.find((bts, i) => {
    if (!itemsSelected) return false;
    const item = itemsSelected[i];
    const quantityAvailable = item.quantity - item.assigned;
    const quantityToAssign = bts.reduce((acc, bt) => acc + bt.units * bt.boxes, 0);
    return quantityToAssign > quantityAvailable;
  }), [packingGroupId, itemsSelected, boxTypes]);

  if (items.length < 1) return null;

  return (
    <Modal
      open={open}
      closeOnOutsideClick={false}
      onClose={onClose}
    >
      <ModalHeader className={styles.header}>
        <IconBoxPlaceItem size={32} style={{ color: 'var(--color-primary)' }}/>
        {index !== undefined ? (
          <><span className="strong">Pack Group {index + 1}</span> | {totalUnits} Total Units</>
        ) : (
          <>{totalUnits} Total Units</>
        )}
      </ModalHeader>
        {selectingItems
          ? <SelectItems
              topRowContent={<><IconInfo /> Select the items you would like to bulk assign.<span className={styles.spacer} /></>}
              items={items}
              setItems={setItems}
              selectAll={selectAll}
            />
          : <AssignItems
              boxTypes={boxTypes}
              items={itemsSelected}
              newBoxType={newBoxType}
              setBoxTypes={setBoxTypes}
            />
        }
      <ModalFooter className={styles.footer}>
        <Button type="secondary" onClick={onClose}>Cancel</Button>
        {selectingItems
          ? <Button onClick={continueClicked} disabled={itemsSelectedCount < 1}>Continue</Button>
          : (
            <Tooltip
              title={zeroItemsToAssign
                ? "Add units to boxes in order to proceed"
                : tooManyAssigned
                ? "Correct the errors on this page in order to proceed"
                : null
              }
              delayMs={0}
            >
              <Button
                onClick={assignClicked}
                disabled={tooManyAssigned || zeroItemsToAssign}
                disableWhileOnClick={true}
              >
                Assign
              </Button>
            </Tooltip>
          )
        }
      </ModalFooter>
    </Modal>
  );
}

type AssignItemsProps = {
  items: Item[];
  boxTypes: BoxType[][];
  newBoxType: (i: number) => void;
  setBoxTypes: (boxTypes: BoxType[][]) => void;
};

const AssignItems = ({
  items,
  newBoxType,
  boxTypes,
  setBoxTypes,
}: AssignItemsProps) => {
  return (
    <ModalBody className={styles.body}>
      <div className={styles.items}>
        {items.map((item, i) => {
          const quantityAvailable = item.quantity - item.assigned;
          const quantityToAssign = boxTypes[i].reduce((acc, bt) => acc + bt.units * bt.boxes, 0);
          const errorText = quantityToAssign > quantityAvailable
            ? `The total count across all rows (${quantityToAssign}) exceeds the available quantity of this item (${quantityAvailable})`
            : null;
          return (
            <div
              key={i}
              className={`${styles.item} ${errorText ? styles.error : ""}`}
              style={{ flexDirection: "column" }}
            >
              <ProductView
                item={item.batchItem}
                quantity={item.quantity}
                showAsin={false}
                showCondition={false}
                small={true}
                tooltipProps={{ placement: "bottom" }}
                content1={
                  <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end"}}>
                    <Button type="link" onClick={() => newBoxType(i)}><IconPlusCircle /> New Box Type</Button>
                  </div>
                }
                content2={
                  <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end"}}>
                    <span className="inline-notice"><IconCaution />&nbsp;{item.assigned} / {item.quantity}</span>
                  </div>
                }
              />
              {boxTypes?.[i].map((bt, j) => (
                <BoxTypeView
                  key={j}
                  boxType={bt}
                  showDelete={boxTypes[i].length > 1}
                  setBoxType={bt => {
                    boxTypes[i][j] = bt;
                    setBoxTypes([...boxTypes]);
                  }}
                  deleteBoxType={() => {
                    boxTypes[i].splice(j, 1);
                    setBoxTypes([...boxTypes]);
                  }}
                />
              ))}
              <span className={styles.errorText}>{errorText}&nbsp;</span>
            </div>
          );
        })}
      </div>
    </ModalBody>
  );
};

type BoxTypeViewProps = {
  boxType: BoxType;
  showDelete: boolean;
  setBoxType: (bt: BoxType) => void;
  deleteBoxType: () => void;
};

function BoxTypeView({showDelete, setBoxType, deleteBoxType, boxType: bt}: BoxTypeViewProps) {
  return (
    <div className={styles.boxType}>
      <div>
        <div className={styles.label}>Units per box</div>
        <InputNumber value={bt.units} onChange={n => setBoxType({...bt, units: n})} />
      </div>
      <div>
        <div className={styles.label}>Total # of boxes</div>
        <InputNumber value={bt.boxes} onChange={n => setBoxType({...bt, boxes: n})} />
      </div>
      <div>
        <div className={styles.label}>Total units</div>
        <InputNumber value={bt.boxes * bt.units} showControls={false} disabled={true} />
      </div>
      <div>
        <div className={styles.label}>Dimensions</div>
        <div className={styles.inlineInputs}>
          <InputNumber
            value={bt.length}
            onChange={n => setBoxType({...bt, length: n})}
            className={styles.smallInput}
            showControls={false}
            suffix={<span className="weak">in</span>}
          />
          &nbsp;&times;&nbsp;
          <InputNumber
            value={bt.width}
            onChange={n => setBoxType({...bt, width: n})}
            className={styles.smallInput}
            showControls={false}
            suffix={<span className="weak">in</span>}
          />
          &nbsp;&times;&nbsp;
          <InputNumber
            value={bt.height}
            onChange={n => setBoxType({...bt, height: n})}
            className={styles.smallInput}
            showControls={false}
            suffix={<span className="weak">in</span>}
          />
        </div>
      </div>
      <div>
        <div className={styles.label}>Weight</div>
        <div className={styles.inlineInputs}>
          <InputNumber
            value={bt.lb}
            onChange={n => setBoxType({...bt, lb: n})}
            className={styles.smallInput}
            showControls={false}
            suffix={<span className="weak">lb</span>}
          />
          &nbsp;
          <InputNumber
            value={bt.oz}
            onChange={n => setBoxType({...bt, oz: n})}
            className={styles.smallInput}
            showControls={false}
            suffix={<span className="weak">oz</span>}
          />
        </div>
      </div>
      <div className={styles.oversized}>
        <div className={styles.label}>&nbsp;</div>
        <div style={{display: "flex"}}>
          <label>
            <Checkbox
              value={bt.oversized}
              onChange={n => setBoxType({...bt, oversized: n})}
            />
            Oversized&nbsp;
            <Tooltip title={"If your box is oversized, please ensure it meets all of Amazons rules for oversized products"}>
              <span className={styles.icon}><IconInfo style={{ marginRight: "1ch" }} /></span>
            </Tooltip>
          </label>
          {showDelete && <>&nbsp;<Button type="text" onClick={deleteBoxType}><IconDelete size={24} /></Button></>}
        </div>
      </div>
    </div>
  );
}

type BulkAssignArgs = {
  batch: FbaBatch;
  setCurrentBatch: (batch: FbaBatch) => void,
  onError: (errors: any) => void,
  packingGroupId: string,
  shipmentId?: number,
  items: FbaBatchItem[];
  boxTypes: BoxType[][];
  printFNSKULabel: PrinterContextProps["printFNSKULabel"],
};

async function doBulkAssign({
  batch,
  setCurrentBatch,
  onError,
  packingGroupId,
  shipmentId,
  items,
  boxTypes,
  printFNSKULabel,
}: BulkAssignArgs) {
  const boxes: Partial<FBABoxDTO>[] = [];
  const totalQuantities = boxTypes.map(bts => bts.reduce((acc, bt) => acc + bt.units * bt.boxes, 0));
  for (const [i, item] of items.entries()) {
    for (const bt of boxTypes[i]) {
      if (bt.units * bt.boxes === 0) continue;
      boxes.push(...Array.from({length: bt.boxes}).map(() => ({
        shipmentId,
        packingGroupId,
        width: bt.width,
        height: bt.height,
        length: bt.length,
        weight: bt.lb + bt.oz / 16,
        items: [
          {
            batchItemId: item.id,
            quantity: bt.units,
            sku: item.sku,
            quantityInCase: 1, // deprecated
          }
        ]
      })));
    }
  }

  try {
    await handleCreateBox(batch, setCurrentBatch, boxes as any, null, shipmentId);

    if (batch.printFNSKUAfterAssigningToBox) {

      for (const [i, item] of items.entries()) {
        if (totalQuantities[i] === 0) continue;
        const packingGroup = batch.chosenPackingOption.packingGroups.find(pg => pg.inboundPackingGroupId === packingGroupId);
        const packingItem = packingGroup.items.find(pi => pi.msku === item.sku);
        printFNSKULabel(
          packingItem.fnsku,
          item.name,
          item.condition,
          totalQuantities[i],
          item.expirationDate
        );
      }
    }
  }
  catch (errors) {
    onError(errors);
  }
}
